import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';
import s from './PhotobookBanner.module.scss';
import photobookImg from './photobook-autumn-2026.webp';
import backgroundImg from './banner-autumn-2026.webp';
import { trackEvent } from '../../services/analytics';
import isOnboardingPath from '../../utils/isOnboardingPath';
import isHomePath from '../../utils/isHomePath';
import isPayoutPath from '../../utils/isPayoutPath';
import isPhotobookPath from '../../utils/isPhotobookPath';
import isEnglishCountry from '../../utils/isEnglishCountry';

// The entry quiz for the current launch. `source=banner` is stored on the
// entry, so banner entries can be told apart from email and ad traffic.
const LAUNCH_KEY = 'autumn-2026-book';
const CAMPAIGN = 'photobook_autumn_2026';

const messages = defineMessages({
  eyebrow: {
    id: 'photobookBannerEyebrowAutumn26',
    defaultMessage: 'Entries are open for the',
  },
  title: {
    id: 'photobookBannerTitleAutumn26',
    defaultMessage: 'Autumn 2026',
  },
  subtitle: {
    id: 'photobookBannerSubtitle',
    defaultMessage: 'Cutest Pets Photobook',
  },
  cta: {
    id: 'photobookBannerCta',
    defaultMessage: 'Submit Your Pet',
  },
});

// A maple leaf, drawn once and reused at different sizes and angles.
const Leaf = ({ className, id }) => (
  <svg className={className} viewBox="0 0 100 100" aria-hidden="true">
    <defs>
      <linearGradient id={id} x1="0" y1="0" x2="1" y2="1">
        <stop offset="0" stopColor="#ff9a2e" />
        <stop offset="0.55" stopColor="#f2581f" />
        <stop offset="1" stopColor="#d9301a" />
      </linearGradient>
    </defs>
    <path
      fill={`url(#${id})`}
      d="M50 3 57 21 67 15 64 39 79 27 83 35 97 32 90 48 96 52 72 70 76 80 54 76 54 97 46 97 46 76 24 80 28 70 4 52 10 48 3 32 17 35 21 27 36 39 33 15 43 21Z"
    />
    <path
      d="M50 92V30M50 62 30 47M50 62 70 47M50 46 38 30M50 46 62 30"
      stroke="#b2290f"
      strokeWidth="2.2"
      strokeLinecap="round"
      fill="none"
      opacity="0.55"
    />
  </svg>
);

const PhotobookBanner = () => {
  const { formatMessage } = useIntl();
  const location = useLocation();
  const isLoggedIn = useSelector((state) => Boolean(state.user && state.user.login));
  const country = useSelector((state) => state.app.currentCountry);
  const locale = (country && country.code.toLowerCase()) || 'us';

  // The quiz and the shop are English-only.
  if (!isEnglishCountry(country)) return null;
  // Keep the sign-up flow distraction-free — no promo banner.
  if (isOnboardingPath(location.pathname)) return null;
  // No promo above the prize claim / withdrawal pages either.
  if (isPayoutPath(location.pathname)) return null;
  // Inside the entry quiz the banner would advertise the page you are on.
  if (isPhotobookPath(location.pathname)) return null;
  // Logged-out homepage visitors (esp. cold FB-ad traffic) get the acquisition
  // hero instead — no competing promo above it.
  if (!isLoggedIn && isHomePath(location.pathname)) return null;

  const destination = `/${locale}/photobook/${LAUNCH_KEY}?source=banner`;

  const handleClick = () => {
    trackEvent('photobook_banner_clicked', { campaign: CAMPAIGN, destination });
  };

  return (
    <div className={s.container}>
      <div className={s.banner}>
        <div className={s.clip}>
          <img className={s.photo} src={backgroundImg} alt="" />
          <div className={s.haze} />
          <div className={s.glow} />
        </div>
        <div className={s.row}>
          <img
            className={s.book}
            src={photobookImg}
            alt="Cutest Pets of Autumn 2026 Photobook"
          />
          <div className={s.center}>
            <div className={s.textBlock}>
              <Leaf className={s.leafLeft} id="pbLeafA" />
              <Leaf className={s.leafRight} id="pbLeafB" />
              <Leaf className={s.leafTop} id="pbLeafC" />
              <Leaf className={s.leafTiny} id="pbLeafD" />
              <div className={s.eyebrow}>{formatMessage(messages.eyebrow)}</div>
              <div className={s.title}>{formatMessage(messages.title)}</div>
              <div className={s.subtitle}>{formatMessage(messages.subtitle)}</div>
            </div>
          </div>
          <span className={s.cta}>
            {formatMessage(messages.cta)}
            <svg
              className={s.arrow}
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="3.2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M5 12h14M13 6l6 6-6 6" />
            </svg>
          </span>
        </div>
        <Link
          to={destination}
          className={s.link}
          aria-label="Submit your pet for the Autumn 2026 Cutest Pets Photobook"
          onClick={handleClick}
        />
      </div>
    </div>
  );
};

export default PhotobookBanner;
